// @ts-strict-ignore
import React, { useContext, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Separator,
} from '@usealloy/component-library';
import { IsolatedEditorContainer } from 'flow/editor/styles';
import {
  StyledTipCallout,
  StyledAggregationCriteriaContainer,
  StyledAggregationSubEditorLayout,
  StyledAggregationSubEditorRow,
  StyledAggregationSubEditorActions,
} from './styles';
import type { AggregationConfigFormFields } from './types';
import BaseAggregationsSection from './BaseAggregationsSection';
import AggregationNameAndDescription from './AggregationNameAndDescription';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectRuleBuilderActiveSubEditor,
  selectThresholdById,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { RuleEditorContext } from '../NodeEditorDrawer/context';
import { populateThresholdData } from 'flow/service/thresholds';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';

type AggregationsSubEditorContainerProps = {
  onClose: () => void;
};

// TODO: Update with follow-up tickets in OAK-1060 epic
const INITIAL_AGGREGATION_CONFIG: AggregationConfigFormFields = {
  name: 'Entities',
  description: '',
  aggregation: 'count_distinct',
  subject: 'entities',
  scope: 'all',
  timeframeFrom: 1,
  timeframeTo: 90,
  timeframeUnit: 'days',
};

const AggregationsSubEditorContainer = ({ onClose }: AggregationsSubEditorContainerProps) => {
  const dispatch = useAppDispatch();
  const { setIsolatedEditingBlock } = useContext(RuleEditorContext);
  const { thresholdId } = useAppSelector(selectRuleBuilderActiveSubEditor);
  const threshold = useAppSelector(selectThresholdById(thresholdId));
  const { actions } = useAppSelector(selectWorkflowData);
  const { reasonCodeGroups, services } = useAppSelector(selectWorkflowProps);

  const [aggregationConfigFormFields, setAggregationConfigFormFields] = useState(
    INITIAL_AGGREGATION_CONFIG,
  );

  const handleSave = () => {
    if (!threshold) {
      onClose();
      return;
    }

    const populatedThreshold = populateThresholdData({
      actions,
      reasonCodeGroups,
      services,
      threshold,
    });
    // TODO: Replace temporary defaults -- OAK-1062 provides real aggregation config output.
    if (!populatedThreshold.operator) {
      dispatch(updateThreshold.operator(populatedThreshold.publicId, 'gt'));
    }
    if (populatedThreshold.right.attributeValue === null
        || populatedThreshold.right.attributeValue === ''
    ) {
      dispatch(updateThreshold.rightValue(populatedThreshold.publicId, 0));
    }

    onClose();
  };

  const clearAggregationsSubEditorState = () => {
    onClose();
    setIsolatedEditingBlock({
      originPublicId: undefined,
      originType: undefined,
      originCaretIndex: undefined,
    });
  };

  return (
    <IsolatedEditorContainer>
      <StyledAggregationSubEditorLayout>
        <StyledAggregationSubEditorRow>
          <StyledAggregationCriteriaContainer>
            <StyledTipCallout text="Build your custom aggregation by selecting the options below." />
            <BaseAggregationsSection
              formFields={aggregationConfigFormFields}
              onFormFieldChange={setAggregationConfigFormFields}
            />
          </StyledAggregationCriteriaContainer>
          <AggregationNameAndDescription
            formFields={aggregationConfigFormFields}
            onFormFieldChange={setAggregationConfigFormFields}
          />
        </StyledAggregationSubEditorRow>
        <Separator />
        <StyledAggregationSubEditorActions>
          <Button
            size={ButtonSize.small}
            variant={ButtonVariant.secondary}
            onClick={clearAggregationsSubEditorState}
          >
            Cancel
          </Button>
          <Button
            size={ButtonSize.small}
            variant={ButtonVariant.primary}
            onClick={handleSave}
          >
            Done
          </Button>
        </StyledAggregationSubEditorActions>
      </StyledAggregationSubEditorLayout>
    </IsolatedEditorContainer>
  );
};

export default AggregationsSubEditorContainer;
